import { useElementSize } from '@mantine/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Button,
  ButtonBase,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { getIconButtonSx } from 'components/Search/styles';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { memo, useEffect, useRef, useState } from 'react';
import { isDeepEqual } from 'remeda';
import { safeMaxDateTime, safeMinDateTime, UTC_MM_DD_YY_HHMM } from 'utility/constants';
import { formatDate } from 'utility/functions';
import { getCommonPopoutSx, getSolidThemeGray } from 'utility/styles';
import { v4 as uuidv4 } from 'uuid';
import {
  dropdownInputAnchorOrigin,
  dropdownInputElevation,
  dropdownInputTransformOrigin,
  getCloseButtonSx,
  getDropdownInputThreshold,
  getFooterContainerSx,
  getFormControlSx,
  getHeaderLabelTypoSx,
  getInfoTextIconSx,
  getRemoveButtonSx,
  getSelectSx,
  getTooltipSx,
  maxPopoverHeightVh,
  maxPopoverWidthPxDateRange,
  tooltipPopperProps,
} from './dropdownConsts';
import { PossibleEvent } from './SelectDropdown/SelectDropdownInput';
import { SelectRenderValueElem } from './SelectDropdown/SelectRenderValueElem';

// This is the original DateRangeDropdownInput.tsx that does NOT support relative date/time expressions.

// Eventually we will delete this and migrate all to TextDateRangeDropdownInput
// so that the server can handle timezones, saved search relative dates, etc.

// todo: does this needs shifting with site timezone as well?
// currently component is unused on this branch so will leave as-is

// Extend dayjs with UTC and timezone plugins.
dayjs.extend(timezone);
dayjs.extend(utc);

type DoubleStringArr = [string, string];
type ZuluTimeString = string;

type DateRangeDropdownInputProps = {
  inputLabel?: string;
  value?: DoubleStringArr;
  updateValue: (event: PossibleEvent | any, value: DoubleStringArr) => void;
  formControlSx?: SxProps<Theme>;
  popoverPaperSx?: SxProps<Theme>;
  formControlVariant?: 'standard' | 'outlined';
  renderValueTooltip?: boolean;
  renderValueDisplay?: 'label' | 'chips';
  popoverMatchesFormControlWidth?: boolean;
  applyChangesOnPopoverClose?: boolean;
  inputPlaceholderText?: string;
  disabled?: boolean;
  hidden?: boolean;
  /** If the end time is empty when a valid date is captured, should the time be set to the end of the day? */
  defaultEndToEOD?: boolean;
  infoText?: string;
  timePicker?: boolean;
};

export const baseDateFmtOpts: Intl.DateTimeFormatOptions = {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'UTC',
};

export function checkValidDate(date: string) {
  return date === '' || dayjs(date).isValid();
}

export function checkValidDateRange(range: DoubleStringArr) {
  return checkValidDate(range[0]) && checkValidDate(range[1]);
}

export function getDateFormatOptions(includeTime: boolean): Intl.DateTimeFormatOptions {
  if (includeTime) {
    return baseDateFmtOpts;
  }

  const { timeStyle, ...dateOnlyOpts } = baseDateFmtOpts;
  return dateOnlyOpts;
}

export function getFormattedDateRangeLabel(range: DoubleStringArr, dateTime = true) {
  const [startDate, endDate] = range;
  const hasStart = startDate !== '';
  const hasEnd = endDate !== '';
  const isValidDateRange = checkValidDateRange(range);
  const startIsValid = hasStart && checkValidDate(startDate);
  const endIsValid = hasEnd && checkValidDate(endDate);
  const isEndBeforeStart = Boolean(
    startIsValid && endIsValid && dayjs(endDate).isBefore(startDate),
  );

  if (range.join('') === '') {
    return '';
  }

  if (isEndBeforeStart) {
    return 'End date cannot be before Start date';
  }

  if (!isValidDateRange) {
    return 'Invalid or incomplete dates.';
  }

  if (startIsValid && endIsValid) {
    const startString = formatDate({ date: startDate, format: UTC_MM_DD_YY_HHMM });
    const endString = formatDate({ date: endDate, format: UTC_MM_DD_YY_HHMM });
    return `${startString} to ${endString}`;
  }

  if (startIsValid) {
    const startString = formatDate({ date: startDate, format: UTC_MM_DD_YY_HHMM });
    return `From ${startString}`;
  }

  if (endIsValid) {
    const endString = formatDate({ date: endDate, format: UTC_MM_DD_YY_HHMM });
    return `Through ${endString}`;
  }

  return '';
}

const getDatePart = (dateTime: string) => {
  const date = dayjs(dateTime).utc();
  const result = date.isValid() ? date.format('YYYY-MM-DD') : '';
  return result;
};

const getTimePart = (dateTime: string, fallbackToEOD = false) => {
  const date = dayjs(dateTime).utc();
  const invalidFallback = fallbackToEOD ? '23:59' : '00:00';
  const result = date.isValid() ? date.format('HH:mm') : invalidFallback;
  return result;
};

const combineDateTime = (date: string, time?: string): ZuluTimeString => {
  if (!date) return '';
  return time ? dayjs(`${date}T${time}Z`).utc().format() : dayjs(date).utc().format();
};

export const DateRangeDropdownInput = memo(
  ({
    inputLabel = 'Select Date Range',
    value = ['', ''],
    inputPlaceholderText = inputLabel,
    updateValue,
    formControlSx,
    popoverPaperSx,
    formControlVariant = 'standard',
    renderValueTooltip,
    renderValueDisplay = 'label',
    infoText = '',
    popoverMatchesFormControlWidth = false,
    applyChangesOnPopoverClose = false,
    disabled = false,
    hidden = false,
    defaultEndToEOD = false,
    timePicker = true,
  }: DateRangeDropdownInputProps) => {
    // UI Vars / Hooks
    const theme = useTheme();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popoverAnchorRef = useRef<HTMLElement | null>(null);
    const { ref: elSizeRef, width: formCtrlWidth } = useElementSize();

    // Clear button workaround for MUI Date/Time internal state
    // they don't expose a method to clear the value so we just have to remount it
    const [pickerKeys, setPickerKeys] = useState({
      startDateKey: uuidv4(),
      startTimeKey: uuidv4(),
      endDateKey: uuidv4(),
      endTimeKey: uuidv4(),
    });

    // Setup consts / state for applyChangesOnPopoverClose prop
    const [stagedValues, setStagedValues] = useState<DoubleStringArr>(['', '']);

    // Sync values passed in to internal state...
    useEffect(() => {
      // Don't update local state if there's nothing to update
      if (isDeepEqual(value, stagedValues)) {
        return;
      }
      // Don't update local state if the popover is open
      // and we're in applyChangesOnPopoverClose mode
      if (popoverOpen && applyChangesOnPopoverClose) {
        return;
      }

      setStagedValues(value); // If we pass the guards, update the local state
    }, [stagedValues, popoverOpen, applyChangesOnPopoverClose, value]);

    // Determine the current values to use for the select - via passed value or local state copy?
    const currentValues = applyChangesOnPopoverClose ? stagedValues : value;

    // Const helpers
    const isDisabled = disabled;
    const isHidden = hidden;
    const hasEnd = Boolean(currentValues[1]);
    const hasStart = Boolean(currentValues[0]);
    const startIsValid = currentValues[0] && checkValidDate(currentValues[0]);
    const endIsValid = currentValues[1] && checkValidDate(currentValues[1]);
    const endDateIsBeforeStartDate = Boolean(
      startIsValid && endIsValid && dayjs(currentValues[1]).isBefore(currentValues[0]),
    );
    const rangeIsValid = checkValidDateRange(currentValues) && !endDateIsBeforeStartDate;

    const endIsError = hasEnd && (endDateIsBeforeStartDate || !endIsValid);
    const startIsError = hasStart && !startIsValid;

    const jsonStringValueOrEmptyStr =
      JSON.stringify(currentValues) === '["",""]' ? '' : JSON.stringify(currentValues);

    const formattedDateLabel = getFormattedDateRangeLabel(currentValues, timePicker);

    // Tooltip logic
    const showTooltip = renderValueTooltip || renderValueDisplay === 'label';
    const tooltipText = showTooltip ? formattedDateLabel : '';
    const tooltipTitle = !popoverOpen ? tooltipText : '';

    // Event handlers
    const handlePopoverOpen = () => {
      setPopoverOpen(true);
    };

    const handlePopoverClose = () => {
      const adjustedValues: DoubleStringArr = [...stagedValues];

      // Clear only the invalid values
      if (startIsError) {
        adjustedValues[0] = '';
      }
      if (endIsError) {
        adjustedValues[1] = '';
      }

      // If the end date is before the start date, clear both values
      if (endDateIsBeforeStartDate) {
        adjustedValues[0] = '';
        adjustedValues[1] = '';
      }

      updateValue(null, adjustedValues);
      setPopoverOpen(false);
    };

    const handleDateTimeChange = (
      newValue: any,
      type: 'start' | 'end',
      isDate: boolean,
      timePicker: boolean,
    ) => {
      const formatted = newValue
        ? dayjs(newValue)
            .utc()
            .format(isDate ? 'YYYY-MM-DD' : 'HH:mm')
        : '';
      const newRange = [...currentValues] as DoubleStringArr;
      const index = type === 'start' ? 0 : 1;

      if (timePicker) {
        newRange[index] = combineDateTime(
          isDate ? formatted : getDatePart(newRange[index]),
          isDate
            ? getTimePart(newRange[index], type === 'end' && defaultEndToEOD)
            : formatted,
        );
      } else {
        newRange[index] = isDate ? formatted : newRange[index];
      }

      if (applyChangesOnPopoverClose) {
        setStagedValues(newRange);
      } else {
        updateValue(null, newRange);
      }
    };

    const handleDateChange = (newDate: any, type: 'start' | 'end') =>
      handleDateTimeChange(newDate, type, true, timePicker);

    const handleTimeChange = (newTime: any, type: 'start' | 'end') =>
      handleDateTimeChange(newTime, type, false, timePicker);

    const handleClear = (event: PossibleEvent) => {
      setPickerKeys({
        startDateKey: uuidv4(),
        startTimeKey: uuidv4(),
        endDateKey: uuidv4(),
        endTimeKey: uuidv4(),
      }); // Remount all inputs on full clear

      if (applyChangesOnPopoverClose) {
        setStagedValues(['', '']);
      } else {
        updateValue(event, ['', '']);
      }
    };

    const handleRemoveStart = () => {
      setPickerKeys((prevKeys) => ({
        startDateKey: uuidv4(),
        startTimeKey: uuidv4(),
        endDateKey: prevKeys.endDateKey,
        endTimeKey: prevKeys.endTimeKey,
      })); // Remount start inputs on start clear

      const newRange: DoubleStringArr = ['', currentValues[1]];
      if (applyChangesOnPopoverClose) {
        setStagedValues(newRange);
      } else {
        updateValue(null, newRange);
      }
    };

    const handleRemoveEnd = () => {
      setPickerKeys((prevKeys) => ({
        startDateKey: prevKeys.startDateKey,
        startTimeKey: prevKeys.startTimeKey,
        endDateKey: uuidv4(),
        endTimeKey: uuidv4(),
      })); // Remount end inputs on end clear

      const newRange: DoubleStringArr = [currentValues[0], ''];
      if (applyChangesOnPopoverClose) {
        setStagedValues(newRange);
      } else {
        updateValue(null, newRange);
      }
    };

    return (
      <Tooltip
        title={tooltipTitle}
        disableInteractive={true}
        disableFocusListener={true}
        slotProps={{
          tooltip: {
            sx: {
              ...getTooltipSx(theme),
            },
          },
        }}
      >
        <FormControl
          size="small"
          variant={formControlVariant}
          ref={elSizeRef}
          disabled={isDisabled}
          hidden={isHidden}
          sx={{
            ...getFormControlSx(theme, popoverOpen),
            ...(typeof formControlSx === 'function'
              ? formControlSx(theme)
              : formControlSx),
            ...(isHidden ? { display: 'none' } : {}),
          }}
        >
          <Select
            ref={popoverAnchorRef}
            multiple={false}
            disabled={isDisabled}
            hidden={isHidden}
            id={`${inputLabel}-daterange-select-input`}
            value={jsonStringValueOrEmptyStr}
            MenuProps={{ open: false }} // use custom popover
            onOpen={handlePopoverOpen}
            open={popoverOpen}
            sx={getSelectSx}
            displayEmpty={true}
            renderValue={() => (
              <SelectRenderValueElem
                selectedChoices={
                  jsonStringValueOrEmptyStr
                    ? [{ value: jsonStringValueOrEmptyStr, label: formattedDateLabel }]
                    : []
                }
                placeholderText={inputPlaceholderText || inputLabel}
                renderValueDisplay={renderValueDisplay}
                isError={!rangeIsValid}
              />
            )}
          >
            {/**
             * To suppress the out-of-range value warning: add the empty default & always have the current value as a choice.
             * for the date we'll put a json string of the current values in the value field.
             */}
            <MenuItem
              value=""
              hidden={true}
              style={{ display: 'none' }}
            />
            <MenuItem
              value={jsonStringValueOrEmptyStr}
              hidden={true}
              style={{ display: 'none' }}
            />
          </Select>

          <Popover
            anchorEl={popoverAnchorRef.current}
            open={popoverOpen && !isDisabled}
            hidden={isHidden}
            onClose={handlePopoverClose}
            anchorOrigin={dropdownInputAnchorOrigin}
            transformOrigin={dropdownInputTransformOrigin}
            elevation={dropdownInputElevation}
            marginThreshold={getDropdownInputThreshold(theme)}
            slotProps={{
              paper: {
                sx: {
                  display: 'grid',
                  gridTemplateRows: 'auto auto 1fr',
                  gridTemplateAreas: `'selectheader' 'selectmainarea' 'selectfooter'`,
                  container: 'selectgrid / normal',
                  overflow: 'hidden',
                  width: popoverMatchesFormControlWidth
                    ? formCtrlWidth
                    : maxPopoverWidthPxDateRange,
                  maxHeight: maxPopoverHeightVh,
                  gap: 0.5,
                  bgcolor: getSolidThemeGray(theme),
                  ...getCommonPopoutSx(theme),
                  ...(typeof popoverPaperSx === 'function'
                    ? popoverPaperSx(theme)
                    : popoverPaperSx),
                },
              },
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gridArea: 'selectheader',
                p: 1,
                borderBottom: `1px solid ${theme.palette.action.selected}`,
              }}
            >
              <Tooltip
                title={infoText}
                arrow={false}
                disableInteractive={true}
                enterDelay={500}
                placement="bottom-start"
                slotProps={{
                  popper: tooltipPopperProps,
                }}
              >
                <Typography
                  sx={getHeaderLabelTypoSx}
                  aria-label={`${inputLabel} - ${infoText}`}
                >
                  {inputLabel}
                  {infoText ? <InfoOutlinedIcon sx={getInfoTextIconSx} /> : null}
                </Typography>
              </Tooltip>
              <Button
                variant="text"
                size="small"
                color="error"
                disabled={!hasStart && !hasEnd}
                startIcon={<RemoveCircleOutlineIcon />}
                sx={getRemoveButtonSx}
                onClick={handleClear}
              >
                Remove
              </Button>
            </Stack>

            <Stack
              direction="column"
              sx={{ gridArea: 'selectmainarea', px: 1 }}
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <DatePicker
                  key={pickerKeys.startDateKey}
                  label="Start Date"
                  value={dayjs(currentValues[0]).utc()}
                  onChange={(newDate) => handleDateChange(newDate, 'start')}
                  disabled={isDisabled}
                  timezone="UTC"
                  maxDate={safeMaxDateTime}
                  minDate={safeMinDateTime}
                  slotProps={{
                    actionBar: {
                      actions: ['cancel', 'accept'],
                    },
                    textField: {
                      error: startIsError,
                      variant: 'outlined',
                      size: 'small',
                      sx: {
                        width: timePicker ? 0 : '100%',
                        flex: 1,
                        minWidth: 0,
                        bgcolor: theme.palette.background.paper,
                      },
                    },
                  }}
                />
                {timePicker && (
                  <>
                    <TimePicker
                      key={pickerKeys.startTimeKey}
                      label="Start Time"
                      value={currentValues[0] ? dayjs(currentValues[0]).utc() : null}
                      timezone="UTC"
                      maxTime={safeMaxDateTime}
                      minTime={safeMinDateTime}
                      onChange={(newTime) => handleTimeChange(newTime, 'start')}
                      disabled={
                        isDisabled ||
                        !currentValues[0] ||
                        !dayjs(currentValues[0]).isValid()
                      }
                      slotProps={{
                        actionBar: {
                          actions: ['cancel', 'accept'],
                        },
                        textField: {
                          error: startIsError,
                          variant: 'outlined',
                          size: 'small',
                          sx: {
                            width: 0,
                            flex: 1,
                            minWidth: 0,
                            bgcolor: theme.palette.background.paper,
                          },
                        },
                      }}
                    />
                  </>
                )}
                <Tooltip title="Clear Start">
                  <IconButton
                    sx={getIconButtonSx}
                    onClick={handleRemoveStart}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <DatePicker
                  key={pickerKeys.endDateKey}
                  label="End Date"
                  value={dayjs(currentValues[1]).utc()}
                  onChange={(newDate) => handleDateChange(newDate, 'end')}
                  disabled={isDisabled}
                  timezone="UTC"
                  maxDate={safeMaxDateTime}
                  minDate={safeMinDateTime}
                  slotProps={{
                    actionBar: {
                      actions: ['cancel', 'accept'],
                    },
                    textField: {
                      error: endIsError,
                      variant: 'outlined',
                      size: 'small',
                      sx: {
                        width: timePicker ? 0 : '100%',
                        flex: 1,
                        minWidth: 0,
                        bgcolor: theme.palette.background.paper,
                      },
                    },
                  }}
                />
                {timePicker && (
                  <>
                    <TimePicker
                      key={pickerKeys.endTimeKey}
                      label="End Time"
                      value={currentValues[1] ? dayjs(currentValues[1]).utc() : null}
                      onChange={(newTime) => handleTimeChange(newTime, 'end')}
                      disabled={
                        isDisabled ||
                        !currentValues[1] ||
                        !dayjs(currentValues[1]).isValid()
                      }
                      timezone="UTC"
                      maxTime={safeMaxDateTime}
                      minTime={safeMinDateTime}
                      slotProps={{
                        actionBar: {
                          actions: ['cancel', 'accept'],
                        },
                        textField: {
                          error: endIsError,
                          variant: 'outlined',
                          size: 'small',
                          sx: {
                            width: 0,
                            flex: 1,
                            minWidth: 0,
                            bgcolor: theme.palette.background.paper,
                          },
                        },
                      }}
                    />
                  </>
                )}
                <Tooltip title="Clear End">
                  <IconButton
                    sx={getIconButtonSx}
                    onClick={handleRemoveEnd}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack
              sx={{
                gridArea: 'selectfooter',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gridTemplateAreas: `"listinfo closebtn"`,
                alignItems: 'center',
                ...getFooterContainerSx(theme, true),
              }}
            >
              {/* Spacer to keep the close button location consistent with other advanced filters */}
              <Box sx={{ gridArea: 'listinfo' }} />

              <ButtonBase
                onClick={handlePopoverClose}
                sx={{
                  gridArea: 'closebtn',
                  ...getCloseButtonSx(theme),
                }}
              >
                Close
              </ButtonBase>
            </Stack>
          </Popover>
        </FormControl>
      </Tooltip>
    );
  },
);

DateRangeDropdownInput.displayName = 'DateRangeDropdownInput';
